import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Skeleton,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { Add, History } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';

import ActiveSessionCard from '../../components/sessions/ActiveSessionCard';
import SessionsTable from '../../components/sessions/SessionsTable';
import SessionsEmptyState from '../../components/sessions/SessionsEmptyState';
import SessionFormDialog from '../../components/sessions/SessionFormDialog';
import ActivateSessionDialog from '../../components/sessions/ActivateSessionDialog';
import CompleteSessionDialog from '../../components/sessions/CompleteSessionDialog';

const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { activeSession, refresh: refreshActive } = useSessionContext();

  const writable = canWrite('sessions');

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);

  const [activateOpen, setActivateOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState<Session | null>(null);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<Session | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sessionsApi.list({ includeArchived });
      setSessions(data);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load sessions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [includeArchived, enqueueSnackbar]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const otherSessions = useMemo(
    () => sessions.filter(s => !activeSession || s._id !== activeSession._id),
    [sessions, activeSession]
  );

  // ── Actions ────────────────────────────────────────────────────────────
  const openCreate = () => { setEditingSession(null); setFormOpen(true); };
  const openEdit   = (s: Session) => { setEditingSession(s); setFormOpen(true); };

  const handleSaved = async () => {
    await loadAll();
    await refreshActive();
  };

  const requestActivate = (s: Session) => { setActivateTarget(s); setActivateOpen(true); };
  const handleActivated = async () => {
    await loadAll();
    await refreshActive();
  };

  const requestComplete = (s: Session) => { setCompleteTarget(s); setCompleteOpen(true); };
  const handleCompleted = async () => {
    await loadAll();
    await refreshActive();
  };

  const handleArchive = async (s: Session) => {
    try {
      await sessionsApi.archive(s._id);
      enqueueSnackbar(`"${s.name}" archived`, { variant: 'success' });
      await loadAll();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not archive session', { variant: 'error' });
    }
  };

  const openSession  = (s: Session) => navigate(`/app/settings/sessions/${s._id}`);
  const promoteToNext = (s: Session) => navigate(`/app/promotions/new?sourceSessionId=${s._id}`);

  // ── Render ─────────────────────────────────────────────────────────────
  const hasAnySessions = sessions.length > 0;
  const showEmptyState = !loading && !hasAnySessions;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Academic Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One active session at a time. Use it to run admissions, attendance, exams, and promotions.
          </Typography>
        </Box>

        {writable && hasAnySessions && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ borderRadius: 2 }}
          >
            New session
          </Button>
        )}
      </Stack>

      {/* Body */}
      {loading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" height={64} sx={{ borderRadius: 2 }} />
        </Stack>
      ) : showEmptyState ? (
        <SessionsEmptyState onCreate={openCreate} />
      ) : (
        <Stack spacing={3}>
          <ActiveSessionCard
            session={activeSession}
            onEdit={openEdit}
            onComplete={requestComplete}
            onOpenSession={openSession}
            onPromoteToNext={promoteToNext}
          />

          {otherSessions.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <History fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Other sessions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    · {otherSessions.length}
                  </Typography>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeArchived}
                      onChange={(_, v) => setIncludeArchived(v)}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">Show archived</Typography>
                  }
                />
              </Stack>
              <SessionsTable
                sessions={otherSessions}
                onActivate={requestActivate}
                onComplete={requestComplete}
                onArchive={handleArchive}
                onEdit={openEdit}
                onOpen={openSession}
              />
            </Box>
          )}

          {!activeSession && hasAnySessions && (
            <Alert severity="info" variant="outlined">
              No session is currently active. Activate one from the table above to allow new enrollments.
            </Alert>
          )}
        </Stack>
      )}

      {/* Dialogs */}
      <SessionFormDialog
        open={formOpen}
        initial={editingSession}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
      <ActivateSessionDialog
        open={activateOpen}
        target={activateTarget}
        currentActive={activeSession}
        onClose={() => setActivateOpen(false)}
        onActivated={handleActivated}
      />
      <CompleteSessionDialog
        open={completeOpen}
        session={completeTarget}
        onClose={() => setCompleteOpen(false)}
        onCompleted={handleCompleted}
      />
    </Box>
  );
};

export default SessionsPage;
